/**
 * Build a wall-mounted upper cabinet for a single wall segment.
 *
 * Upper cabinets are 12" deep × 30" tall by default and hang on the
 * wall starting `UPPER_CABINET_GAP_ABOVE_COUNTER` above the countertop
 * top. Like lower cabinets, they sit flush against the wall with their
 * back face on the wall surface.
 */

const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  UPPER_CABINET_DIMENSIONS,
  UPPER_CABINET_GAP_ABOVE_COUNTER,
} = require('./constants');
const { getWallAxes } = require('./wallMath');

/**
 * The fixture group every cabinet THIS builder mints carries.
 *
 * It is the mark of the plain uppers fill's own product — a box the fill can
 * make again, and therefore one nothing else has to preserve. An over-fridge
 * cabinet is built here too but the install path RE-STAMPS it
 * (`kitchenFridgeInstall_*`), and a measured one carries no group at all, so
 * the three are told apart by this prefix and only by it. `isPlainFillUpper`
 * lives beside the stamp so the writer and the readers cannot drift.
 */
const PLAIN_UPPER_GROUP_PREFIX = 'kitchenUpperCabinet_';

/**
 * Did the plain uppers fill emit this cabinet?
 *
 * The question a carry-forward has to ask before keeping an upper the fill did
 * not re-emit this solve: if the fill made it, not re-emitting it was a
 * DECISION, and preserving it overrides that decision. Anything else over the
 * same wall — measured, or an appliance's install piece — has no other
 * producer and must be kept (`mergeResolvedRuns.keepRule`: never delete what
 * you cannot rebuild).
 */
function isPlainFillUpper(obj) {
  return (
    obj?.type === 'upper-cabinet' &&
    typeof obj?.fixtureGroupId === 'string' &&
    obj.fixtureGroupId.startsWith(PLAIN_UPPER_GROUP_PREFIX)
  );
}

/**
 * Where a standard upper's BOTTOM sits over a standard base: floor + base
 * height + countertop + the 18" backsplash gap.
 */
function upperBottomLineY(floorY) {
  return (
    floorY +
    KITCHEN_CABINET_DIMENSIONS.height +
    KITCHEN_COUNTERTOP_THICKNESS +
    UPPER_CABINET_GAP_ABOVE_COUNTER
  );
}

/**
 * The run's UPPER-CABINET LINE: the top edge of a standard upper hung at the
 * standard height. What a tall unit rises to (a tall span's height is this
 * minus the floor — `cabinet-runs/fillRun.js`) and what the over-fridge
 * cabinet lines up with.
 */
function upperTopLineY(floorY) {
  return upperBottomLineY(floorY) + UPPER_CABINET_DIMENSIONS.height;
}

/**
 * @param {Object} wall - Wall with `alignment.bottomLeft/Right`.
 * @param {{start: number, end: number}} segment - Wall-param range (meters from BL).
 * @param {number} floorY - Floor surface Y in world meters.
 * @param {Object} [opts]
 * @param {number} [opts.depth] - Override depth (e.g. for over-fridge cabinets).
 * @param {number} [opts.height] - Override height (e.g. for tall over-fridge cabinets).
 * @param {number} [opts.bottomY] - Override bottom-Y (defaults to `upperBottomLineY(floorY)`).
 * @param {string} [opts.label]
 * @param {boolean} [opts.handleAtBottom] - Render vertical door pulls
 *   near the bottom of the door instead of centered. Used for tall
 *   over-fridge cabinets where the bottom is the only reachable edge.
 * @param {string} [opts.stampTag] - A tag in the id stamp, so a second row
 *   over the same span (a stacked upper, `cabinet-runs/upperStack.js`) can
 *   never share an id with the box under it. Still a plain-fill stamp.
 * @returns {Object} New upper-cabinet object.
 */
function buildUpperCabinetForSegment(wall, segment, floorY, opts = {}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const { start, wallDir, wallNormal: normal, wallRotationY: ry } = axes;

  const segmentWidth = segment.end - segment.start;
  const segmentMid = (segment.start + segment.end) / 2;

  const surfacePoint = {
    x: start.x + segmentMid * wallDir.x,
    z: start.z + segmentMid * wallDir.z,
  };

  const cabinetDepth = opts.depth ?? UPPER_CABINET_DIMENSIONS.depth;
  const cabinetHeight = opts.height ?? UPPER_CABINET_DIMENSIONS.height;

  const bottomY = opts.bottomY ?? upperBottomLineY(floorY);

  const cabinetCenter = {
    x: surfacePoint.x + (cabinetDepth / 2) * normal.x,
    y: bottomY + cabinetHeight / 2,
    z: surfacePoint.z + (cabinetDepth / 2) * normal.z,
  };

  const stamp = `${opts.stampTag ? `${opts.stampTag}_` : ''}${wall.id}_${Math.round(segment.start * 1000)}`;
  const fixtureGroupId = `${PLAIN_UPPER_GROUP_PREFIX}${stamp}`;
  const cabinetId = `upper_cabinet_${stamp}`;

  return {
    id: cabinetId,
    type: 'upper-cabinet',
    label: opts.label || 'Kitchen Upper Cabinet',
    category: { 'upper-cabinet': true },
    center: cabinetCenter,
    dimensions: {
      x: segmentWidth,
      y: cabinetHeight,
      z: cabinetDepth,
      ...(opts.handleAtBottom ? { handleAtBottom: true } : {}),
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wall.id],
    fixtureGroupId,
    customFrontClearance: 0,
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
  };
}

module.exports = {
  buildUpperCabinetForSegment,
  upperBottomLineY,
  upperTopLineY,
  isPlainFillUpper,
  PLAIN_UPPER_GROUP_PREFIX,
};
